/**
 * EditModalWrapper Component
 *
 * Reusable modal shell for all edit modals.
 * Handles: Modal state, BookingContext setup, cancel wiring.
 */

import { type JSX } from 'react';
import { tzdate } from '@repo/tzdate';
import { CartItem } from '@repo/types';
import Modal from 'src/components/common/modal/Modal';
import { BookingContextProvider } from 'src/widgets/BookingContext';
import { formatTime } from '@repo/widget-utils/DateHelpers';

export type EditModalWrapperProps = {
    /** Cart item being edited */
    cartItem: CartItem;

    /** Anchor element for modal */
    anchorEl: HTMLElement | null;

    /** Cancel handler (closes modal + cleanup) */
    onCancel: () => void;

    /** Children render function - receives nothing, renders content */
    children: JSX.Element;
};

/**
 * Wraps edit modals with Modal and BookingContext
 */
export function EditModalWrapper(props: EditModalWrapperProps): JSX.Element {
    const { cartItem, anchorEl, onCancel, children } = props;

    // Setup initial time slot for BookingContext
    const startTime = tzdate(cartItem.products[0].start);
    const endTime = tzdate(cartItem.products[0].end);
    const durationHours = endTime.diff(startTime, 'hours');
    const shouldShowEndTime = durationHours < 24;

    const initialSelectedTimeSlot = {
        label: formatTime(startTime),
        sublabel: shouldShowEndTime ? formatTime(endTime) : undefined,
        product: cartItem.products[0],
        products: cartItem.products[0]?.timeslots ?? [],
    };

    return (
        <BookingContextProvider
            initialTicketOptions={cartItem.ticketOptions}
            initialProducts={cartItem.products}
            initialTimeSlot={initialSelectedTimeSlot}
        >
            <Modal
                open={Boolean(anchorEl)}
                refocusElementOnClose={{ current: anchorEl }}
                onClose={onCancel}
            >
                {children}
            </Modal>
        </BookingContextProvider>
    );
}
